// Tiptap document helpers. Logic is byte-identical to the legacy frontend
// so server-stored JSON keeps round-tripping cleanly.

export const RICH_TEXT_FORMAT = 'tiptap-json';

type RichTextNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: RichTextNode[];
};

type RichTextDocument = {
  type: 'doc';
  content: RichTextNode[];
};

const blockNodeTypes = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'codeBlock',
  'blockquote',
]);

const appendText = (parts: string[], value?: string | null) => {
  if (!value) return;
  if (parts.length > 0 && !parts[parts.length - 1].endsWith('\n')) {
    parts.push(' ');
  }
  parts.push(value);
};

const walkText = (node: RichTextNode | null | undefined, parts: string[]) => {
  if (!node) return;
  if (node.type === 'text') {
    appendText(parts, node.text ?? '');
    return;
  }
  if (node.type === 'hardBreak') {
    parts.push('\n');
    return;
  }
  if (node.type === 'mention') {
    const label = typeof node.attrs?.label === 'string' ? node.attrs.label : '';
    appendText(parts, label ? `@${label}` : '@участник');
    return;
  }

  node.content?.forEach((child) => walkText(child, parts));

  if (node.type && blockNodeTypes.has(node.type) && parts.length > 0 && !parts[parts.length - 1].endsWith('\n')) {
    parts.push('\n');
  }
};

const normalizeParagraphText = (text: string) => text.replace(/\r/g, '').trim();

export const createRichTextDocument = (text?: string | null): RichTextDocument => {
  const normalized = (text ?? '').replace(/\r/g, '');
  const paragraphs = normalized.split('\n').map(normalizeParagraphText).filter(Boolean);

  if (!paragraphs.length) {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    };
  }

  return {
    type: 'doc',
    content: paragraphs.map((paragraph) => ({
      type: 'paragraph',
      content: [{ type: 'text', text: paragraph }],
    })),
  };
};

export const canonicalizeRichText = (raw?: string | null) => {
  if (!raw?.trim()) {
    return JSON.stringify(createRichTextDocument());
  }
  try {
    return JSON.stringify(JSON.parse(raw));
  } catch {
    return JSON.stringify(createRichTextDocument(raw));
  }
};

export const ensureRichText = (raw?: string | null, fallbackText?: string | null) => {
  if (raw?.trim()) {
    return canonicalizeRichText(raw);
  }
  return JSON.stringify(createRichTextDocument(fallbackText));
};

export const extractPlainTextFromRichText = (raw?: string | null) => {
  const content = ensureRichText(raw);
  try {
    const parsed = JSON.parse(content) as RichTextDocument;
    const parts: string[] = [];
    walkText(parsed, parts);
    return parts.join('').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  } catch {
    return (raw ?? '').trim();
  }
};

export const extractMentionIdsFromRichText = (raw?: string | null) => {
  const ids = new Set<number>();
  const visit = (node: RichTextNode | null | undefined) => {
    if (!node) return;
    if (node.type === 'mention') {
      const id = Number(node.attrs?.id);
      if (Number.isFinite(id)) ids.add(id);
    }
    node.content?.forEach(visit);
  };

  try {
    visit(JSON.parse(ensureRichText(raw)) as RichTextDocument);
  } catch {
    return [];
  }

  return [...ids];
};

const dataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const ext = (() => {
    const match = /^data:([^;]+);/.exec(dataUrl);
    const mime = match?.[1] ?? blob.type ?? '';
    if (mime === 'image/png') return '.png';
    if (mime === 'image/jpeg' || mime === 'image/jpg') return '.jpg';
    if (mime === 'image/gif') return '.gif';
    if (mime === 'image/webp') return '.webp';
    if (mime === 'image/svg+xml') return '.svg';
    return '';
  })();
  const safeName = /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName || 'image'}${ext}`;
  return new File([blob], safeName, { type: blob.type });
};

/**
 * Walk a rich-text JSON document, find any image nodes whose src is a data: URL,
 * upload each via the supplied uploader, and rewrite the src to the returned URL.
 * Returns the updated JSON string (unchanged if there were no data: URLs).
 */
export const flushDataUrlImages = async (
  content: string,
  uploader: (file: File) => Promise<string>,
): Promise<string> => {
  if (!content || !content.includes('data:')) return content;
  let doc: RichTextDocument;
  try {
    doc = JSON.parse(content) as RichTextDocument;
  } catch {
    return content;
  }

  const promises: Promise<void>[] = [];
  let mutated = false;
  const walk = (node: RichTextNode | null | undefined) => {
    if (!node) return;
    if (node.type === 'image') {
      const src = node.attrs?.src;
      if (typeof src === 'string' && src.startsWith('data:')) {
        const dataUrl = src;
        const altRaw = node.attrs?.alt;
        const alt = typeof altRaw === 'string' && altRaw.trim() ? altRaw : 'image.png';
        promises.push(
          (async () => {
            try {
              const file = await dataUrlToFile(dataUrl, alt);
              const url = await uploader(file);
              if (url) {
                if (!node.attrs) node.attrs = {};
                node.attrs.src = url;
                mutated = true;
              }
            } catch {
              // Leave src untouched on failure; caller may retry on next save.
            }
          })(),
        );
      }
    }
    node.content?.forEach(walk);
  };
  walk(doc);
  await Promise.all(promises);
  return mutated ? JSON.stringify(doc) : content;
};
